import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import LinearProgress from "@mui/material/LinearProgress";
import TablePagination from "@mui/material/TablePagination";
import CircularProgress from "@mui/material/CircularProgress";
import TableSortLabel from "@mui/material/TableSortLabel";

//All Styles
import { makeStyles } from "tss-react/mui";

import { SeverityPill } from "./severity-pill";
import { Link } from "react-router-dom";
import { convertTimezone } from "../../../../util/common";

const statusMap = {
  success: "success",
  error: "error",
  failed: "error",
  warning: "warning",
};

const useStyles = makeStyles()((theme) => ({
  pagination: {
    "& p": {
      marginBottom: "0px",
    },
  },
  link: {
    cursor: "pointer",
  },
  lable: {
    fontSize: "0.775rem",
  },
}));

const paginationOptions = [5, 10, 25];

export const OverviewTable = (props) => {
  const {
    header = [],
    data = [],
    sx,
    count = 0,
    limit = 5,
    handleChangePage,
    handleChangeRowsPerPage,
    page = 0,
    loading,
    tableHeight = null,
    handleRowClick = false,
    sortingProperties: { order = "asc", orderBy = "", handleSorting = () => {} },
  } = props;

  const { classes } = useStyles();

  return (
    <Card
      sx={sx}
      style={{
        boxShadow: "rgba(0, 0, 0, 0.04) 0px 5px 22px, rgba(0, 0, 0, 0.03) 0px 0px 0px 0.5px",
        backgroundColor: "rgb(255, 255, 255)",
        color: "rgb(17, 25, 39)",
        transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
        overflow: "hidden",
        borderRadius: "0 0 20px 20px",
        height: "100%",
        width: "100%",
      }}
    >
      <Box sx={{ sx, maxHeight: `${tableHeight}px`, overflow: "auto" }}>
        {header && (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {header.map(({ key, value, sortable, showTimezone }) => {
                  return (
                    <TableCell
                      key={key}
                      {...(sortable
                        ? {
                            sortDirection: orderBy === value ? order : false,
                          }
                        : {})}
                      data-id={key}
                      data-sort-order={orderBy === value ? order : "asc"}
                      data-sort-active={orderBy === value ? true : false}
                    >
                      <>
                        {sortable ? (
                          <TableSortLabel
                            active={orderBy === value}
                            direction={orderBy === value ? order : "asc"}
                            onClick={() => handleSorting(value)}
                            className={classes.lable}
                          >
                            {key}
                          </TableSortLabel>
                        ) : (
                          <>{key}</>
                        )}
                      </>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell
                    style={{ borderBottom: "0px", textAlign: "center", minHeight: "250px", height: "250px" }}
                    rowSpan={5}
                    colSpan={header.length}
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                !!data.length &&
                data.map((tableData, index) => {
                  return (
                    <TableRow
                      hover
                      key={index + 1}
                      {...(typeof handleRowClick === "function"
                        ? { hover: true, onClick: () => handleRowClick(tableData), sx: { cursor: "pointer" } }
                        : {})}
                    >
                      {header.map((tableHeader) => {
                        if (tableHeader.status)
                          return (
                            <TableCell key={tableHeader.key}>
                              <SeverityPill color={statusMap[tableData[tableHeader.value]]}>
                                {tableData[tableHeader.value] || "-"}
                              </SeverityPill>
                            </TableCell>
                          );

                        if (tableHeader.progressbar)
                          return (
                            <TableCell
                              key={tableHeader.key}
                              {...(tableHeader.link
                                ? {
                                    component: Link,
                                    to: tableHeader.linkTo,
                                    className: classes.link,
                                    state: { [tableHeader.linkDataKey]: tableData[tableHeader.linkDataKey] },
                                    onClick: (e) => e.stopPropagation(),
                                  }
                                : {})}
                            >
                              <span style={{ float: "right" }}>
                                {tableHeader.format.replace("=value=", tableData[tableHeader.value])}
                              </span>
                              <Box sx={{ mt: 3 }}>
                                <LinearProgress
                                  value={tableData[tableHeader.value]}
                                  variant="determinate"
                                  color="success"
                                />
                              </Box>
                            </TableCell>
                          );

                        if (tableHeader?.linkTo)
                          return (
                            <TableCell key={tableHeader.key}>
                              <Box
                                {...(tableHeader.link
                                  ? {
                                      component: Link,
                                      to: tableHeader.linkTo,
                                      className: classes.link,
                                      state: {
                                        [tableHeader.linkDataKey]: tableData[tableHeader.linkDataKey],
                                      },
                                      color: "#007bff",
                                      onClick: (e) => e.stopPropagation(),
                                    }
                                  : {})}
                              >
                                {tableData[tableHeader.value] || "-"}
                              </Box>
                            </TableCell>
                          );

                        if (tableHeader?.showTimezone)
                          return (
                            <TableCell key={tableHeader.key}>
                              {![undefined, null, "N/A"].includes(tableData[tableHeader.value])
                                ? convertTimezone(tableData[tableHeader.value])
                                : "-"}
                            </TableCell>
                          );

                        return <TableCell key={tableHeader.key}>{tableData[tableHeader.value] || "-"}</TableCell>;
                      })}
                    </TableRow>
                  );
                })}

              {!loading && !data.length && (
                <TableRow>
                  <TableCell style={{ borderBottom: "0px", textAlign: "center" }} colSpan={header.length}>
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Box>
      <TablePagination
        component="div"
        count={count}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        page={page}
        rowsPerPage={limit}
        rowsPerPageOptions={paginationOptions}
        className={classes.pagination}
      />
      <Divider />
    </Card>
  );
};

OverviewTable.prototype = {
  header: PropTypes.array,
  data: PropTypes.array,
  sx: PropTypes.object,
  count: PropTypes.number,
  limit: PropTypes.number,
  handleChangePage: PropTypes.func,
  handleChangeRowsPerPage: PropTypes.func,
  page: PropTypes.number,
  loading: PropTypes.bool,
  tableHeight: PropTypes.number,
  handleRowClick: PropTypes.func,
  sortingProperties: PropTypes.shape({
    order: PropTypes.oneOf(["asc", "desc"]),
    orderBy: PropTypes.string,
    handleSorting: PropTypes.func,
  }),
};
